import { getOptions, getValue } from '@ourbranch/lookups';

/**
 * Trailer physical-damage deductibles are filed separately from the vehicle's, and lower. CFM tops
 * out at 1000 while the vehicle lists run to 2500, and sending a higher deductible on a trailer
 * makes ClarionDoor fail the WHOLE policy's rate — `Unable to locate factors ... [23544] ...
 * vehicle_or_trailer_code_where => 2` returns premium 0 for every unit, not just the trailer.
 * Confirmed on releases 1504, 1508 and 1512, so it is the filed limit rather than a release defect.
 *
 * A carrier with no `trailer*ByCarrier` list falls back to the vehicle list, leaving other brands
 * exactly as they were. #34400
 */

/** Maps a vehicle deductible lookup name to its trailer-specific counterpart. */
const trailerLookupName = (name) => `trailer${name.charAt(0).toUpperCase()}${name.slice(1)}`;

/**
 * Deductible options for a trailer: the carrier's trailer-specific list when one is filed, otherwise
 * the vehicle list. Always returns an array so callers can `.filter()` the result directly.
 */
export function getTrailerDeductibleOptions(name, state, carrier) {
  const trailerSpecific = getOptions(trailerLookupName(name), state, undefined, undefined, carrier);
  return trailerSpecific ?? getOptions(name, state) ?? [];
}

/**
 * Whether a stored trailer deductible is one the carrier actually files. An empty value passes so
 * the required-field rule owns that message rather than producing two errors for one field.
 */
export function isTrailerDeductibleWithinCap(value, name, state, carrier) {
  if (!value) {
    return true;
  }
  const trailerSpecific = getOptions(trailerLookupName(name), state, undefined, undefined, carrier);
  if (!trailerSpecific) {
    return true;
  }
  return trailerSpecific.some((option) => String(option.id) === String(value));
}

/**
 * Whether a trailer's physical-damage deductibles are unchanged from the policy record being edited,
 * matched by VIN against the form's initialValues.
 *
 * Migrated CFM auto policies were imported with trailer deductibles that match no insured vehicle
 * (the legacy book never harmonized them). The `minimum{Coll,Comp}DeductibleMet` rules require a
 * trailer to match some car's deductibles, so requiring a match blocked staff from saving ANY edit
 * to those policies. Grandfathering a trailer whose deductibles are untouched lets those policies be
 * edited, while a newly-added trailer or any change to a trailer's deductibles still must match.
 * Mirrors the trailer VALUE cap relaxation (`getTrailerValueCap`, #35121). #35431
 *
 * @param {Array<{VIN?: string, deductibleCollision?: string, deductibleComprehensive?: string}>}
 *   originalTrailers the persisted trailers (from the form's initialValues).
 * @param {{VIN?: string, deductibleCollision?: string, deductibleComprehensive?: string}} trailer
 *   the trailer currently being validated.
 */
export function isTrailerDeductibleUnchanged(originalTrailers, trailer) {
  if (!Array.isArray(originalTrailers) || !trailer?.VIN) {
    return false;
  }
  const original = originalTrailers.find((t) => t?.VIN === trailer.VIN);
  if (!original) {
    return false;
  }
  return (
    original.deductibleCollision === trailer.deductibleCollision &&
    original.deductibleComprehensive === trailer.deductibleComprehensive
  );
}

/**
 * Comprehensive deductible options for a trailer, collapsed to plain amounts.
 *
 * A trailer carries no separate glass coverage, so its comprehensive is presented as a plain
 * deductible ($500), never a glass variant ($500 w/ $0 glass). Most states already list plain
 * comprehensive deductibles, but a few (KY, MA) file comprehensive ONLY as glass variants. There the
 * old `.filter(id => !id.includes('GL'))` left the trailer with nothing but No Coverage / $0 — and
 * since the offer/policy check matches a trailer to an insured vehicle on the base deductible
 * (`split('/')[0]`), no glass-carrying vehicle could ever be matched and the trailer could not take
 * physical damage at all. #35294
 *
 * Collapsing each option to its base amount (`split('/')[0]`, de-duplicated) yields the same plain
 * list those normal states already produce, and gives the glass-only states a plain, matchable list
 * instead of an empty one. Options that are already plain are returned untouched so their filed label
 * is preserved; a collapsed glass option is relabeled from the base list (falling back to a currency
 * format for amounts that live only as glass variants, e.g. MA's 300).
 */
export function getTrailerComprehensiveDeductibleOptions(state, carrier) {
  const options = getTrailerDeductibleOptions('deductibleComprehensive', state, carrier);
  const seen = new Set();
  const plain = [];
  for (const option of options) {
    const baseId = String(option.id).split('/')[0];
    if (seen.has(baseId)) {
      continue;
    }
    seen.add(baseId);
    if (baseId === String(option.id)) {
      plain.push(option);
    } else {
      plain.push({ ...option, id: baseId, value: plainComprehensiveLabel(baseId) });
    }
  }
  return plain;
}

/** Plain label for a collapsed comprehensive amount, e.g. `500` -> `$500`, `NONE` -> `No Coverage`. */
function plainComprehensiveLabel(baseId) {
  const fromLookup = getValue('deductibleComprehensive', baseId);
  if (fromLookup) {
    return fromLookup;
  }
  return baseId === 'NONE' ? 'No Coverage' : `$${Number(baseId).toLocaleString('en-US')}`;
}
